import axios, { AxiosInstance } from 'axios';

export interface ApiResponse {
  success: boolean;
  data?: any;
  message?: string;
  error?: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  userType: 'student' | 'employer';
  tier: 'free' | 'basic' | 'plus' | 'premium' | 'pro' | 'enterprise';
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  features: any;
  limits: any;
  isActive: boolean;
}

export interface UserSubscription {
  id: string;
  planId: string;
  status: string;
  billingCycle: 'monthly' | 'yearly';
  paymentMethod: string;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  plan?: SubscriptionPlan;
}

class SubscriptionApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${process.env.REACT_APP_API_URL}/subscriptions`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  // ============================================================================
  // SUBSCRIPTION PLANS
  // ============================================================================

  async getPlans(userType?: 'student' | 'employer'): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (userType) params.append('userType', userType);
    
    const response = await this.api.get(`/plans?${params}`);
    return response.data;
  }

  async getPlanDetails(planId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/plans/${planId}`);
    return response.data;
  }

  // ============================================================================
  // USER SUBSCRIPTIONS
  // ============================================================================

  async getCurrentSubscription(): Promise<ApiResponse> {
    const response = await this.api.get('/current');
    return response.data;
  }

  async getUsageHistory(months = 6): Promise<ApiResponse> {
    const response = await this.api.get(`/usage?months=${months}`);
    return response.data;
  }

  async checkFeatureAccess(featureName: string): Promise<ApiResponse> {
    const response = await this.api.get(`/features/${featureName}`);
    return response.data;
  }

  async checkUsageLimit(usageType: string): Promise<ApiResponse> {
    const response = await this.api.get(`/limits/${usageType}`);
    return response.data;
  }

  // ============================================================================
  // SUBSCRIPTION LIFECYCLE
  // ============================================================================

  async cancelSubscription(immediate = false): Promise<ApiResponse> {
    const response = await this.api.post('/cancel', { immediate });
    return response.data;
  }

  async pauseSubscription(resumeAt?: string): Promise<ApiResponse> {
    const response = await this.api.post('/pause', { resumeAt });
    return response.data;
  }

  async resumeSubscription(): Promise<ApiResponse> {
    const response = await this.api.post('/resume');
    return response.data;
  }

  // ============================================================================
  // PAYMENTS
  // ============================================================================

  async createPaymentIntent(
    planId: string, 
    billingCycle: 'monthly' | 'yearly', 
    paymentMethod: 'stripe' | 'paypal' | 'crypto' | 'free',
    cryptoCurrency?: string
  ): Promise<ApiResponse> {
    const response = await this.api.post('/subscribe/intent', {
      planId,
      billingCycle,
      paymentMethod,
      cryptoCurrency
    });
    return response.data;
  }

  async confirmSubscription(
    paymentIntentId: string,
    paymentMethod: 'stripe' | 'paypal' | 'crypto' | 'free',
    planId: string,
    billingCycle: 'monthly' | 'yearly',
    additionalData?: any
  ): Promise<ApiResponse> {
    const response = await this.api.post('/subscribe/confirm', {
      paymentIntentId,
      paymentMethod,
      planId,
      billingCycle,
      ...additionalData
    });
    return response.data;
  }

  // ============================================================================
  // PAYMENT METHODS
  // ============================================================================

  async getPaymentMethods(): Promise<ApiResponse> {
    const response = await this.api.get('/payment-methods');
    return response.data;
  }

  async addPaymentMethod(paymentMethodData: any): Promise<ApiResponse> {
    const response = await this.api.post('/payment-methods', paymentMethodData);
    return response.data;
  }

  // Check crypto payment status
  async checkPaymentStatus(paymentId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/payment-status/${paymentId}`);
    return response.data;
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  calculateYearlySavings(monthlyPrice: number, yearlyPrice: number): number {
    const annualMonthlyTotal = monthlyPrice * 12;
    return annualMonthlyTotal - yearlyPrice;
  }

  calculateYearlySavingsPercentage(monthlyPrice: number, yearlyPrice: number): number {
    const savings = this.calculateYearlySavings(monthlyPrice, yearlyPrice);
    const annualMonthlyTotal = monthlyPrice * 12;
    return annualMonthlyTotal > 0 ? (savings / annualMonthlyTotal) * 100 : 0;
  }

  formatCurrency(amount: number, currency = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2
    }).format(amount);
  }

  getFeatureDisplayName(featureName: string): string {
    const featureNames: { [key: string]: string } = {
      // Student Features
      allCoursesAccess: 'All Courses Access',
      priorityJobMatching: 'Priority Job Matching',
      advancedAnalytics: 'Advanced Analytics', 
      aiCvGeneration: 'AI CV Generation',
      detailedEmployerInsights: 'Detailed Employer Insights',
      prioritySupport: 'Priority Support',
      customProfileThemes: 'Custom Profile Themes',
      skillAssessmentRetakes: 'Unlimited Skill Assessment Retakes',
      learningPathRecommendations: 'Personalized Learning Paths',
      mentorshipAccess: 'Mentorship Access',
      exclusiveWebinars: 'Exclusive Webinars',
      portfolioBuilder: 'Portfolio Builder',

      // Employer Features
      jobPostingAccess: 'Job Posting Access',
      candidateSearchAccess: 'Candidate Search',
      backgroundCheckAccess: 'Background Check Access',
      socialMediaAnalysisAccess: 'Social Media Analysis',
      customCourseCreation: 'Custom Course Creation',
      advancedFiltering: 'Advanced Candidate Filtering',
      bulkMessaging: 'Bulk Messaging',
      detailedCandidateAnalytics: 'Detailed Candidate Analytics',
      longitudinalDataAccess: 'Longitudinal Learning Data',
      customBranding: 'Custom Company Branding',
      apiAccess: 'API Access',
      dedicatedAccountManager: 'Dedicated Account Manager',
      whitelabelSolution: 'White-label Solution',
      ssoIntegration: 'SSO Integration',
      customIntegrations: 'Custom Integrations'
    };

    return featureNames[featureName] || featureName;
  }

  getLimitDisplayName(limitType: string): string {
    const limitNames: { [key: string]: string } = {
      // Student Limits
      coursesPerMonth: 'Courses per Month',
      jobApplicationsPerMonth: 'Job Applications per Month',
      skillAssessmentsPerMonth: 'Skill Assessments per Month',
      cvGenerationsPerMonth: 'CV Generations per Month',
      portfolioProjects: 'Portfolio Projects',

      // Employer Limits
      jobPostingsPerMonth: 'Job Postings per Month',
      candidateViewsPerMonth: 'Candidate Views per Month',
      messagesPerMonth: 'Messages per Month',
      backgroundChecksPerMonth: 'Background Checks per Month',
      customCoursesTotal: 'Custom Courses',
      teamMembersTotal: 'Team Members',
      dataExportPerMonth: 'Data Exports per Month',
      apiCallsPerMonth: 'API Calls per Month'
    };

    return limitNames[limitType] || limitType;
  }

  formatLimit(limit: number): string {
    if (limit === -1) return 'Unlimited';
    if (limit >= 1000000) return `${(limit / 1000000).toFixed(1)}M`;
    if (limit >= 1000) return `${(limit / 1000).toFixed(1)}K`;
    return limit.toString();
  }

  getPlanColor(tier: string): string {
    const tierColors: { [key: string]: string } = {
      free: 'text-gray-600 bg-gray-100',
      basic: 'text-blue-600 bg-blue-100',
      plus: 'text-indigo-600 bg-indigo-100',
      premium: 'text-purple-600 bg-purple-100',
      pro: 'text-pink-600 bg-pink-100',
      enterprise: 'text-red-600 bg-red-100'
    };

    return tierColors[tier] || 'text-gray-600 bg-gray-100';
  }

  getPlanIcon(tier: string): string {
    const tierIcons: { [key: string]: string } = {
      free: '🆓',
      basic: '📋',
      plus: '⭐',
      premium: '💎',
      pro: '🚀',
      enterprise: '🏢'
    };

    return tierIcons[tier] || '📋';
  }

  isFeatureIncluded(plan: SubscriptionPlan, featureName: string): boolean {
    return plan.features?.[featureName] === true;
  }

  getUsagePercentage(current: number, limit: number): number {
    if (limit === -1) return 0; // Unlimited
    if (limit === 0) return 100; // No limit means 100% used
    return Math.min((current / limit) * 100, 100);
  }

  getUsageColor(percentage: number): string {
    if (percentage >= 90) return 'text-red-600 bg-red-100';
    if (percentage >= 75) return 'text-yellow-600 bg-yellow-100';
    if (percentage >= 50) return 'text-blue-600 bg-blue-100';
    return 'text-green-600 bg-green-100';
  }
}

export default new SubscriptionApiService();